// LIBRERIE/IMPORTS
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

// HOOKS
import { useGetAgencyDetails } from '../../hooks/useGetAgencyDetails';

// COMPONENTS
import WidgetContacts from '../Widgets/WidgetContacts/WidgetContacts';
import { WidgetMail } from '../Widgets/WidgetMail/WidgetMail';
import SubHeaderDefault from '../Header/subHeader/SubHeaderDefault';
import ServerError from '../Error/ServerError';
import LoadingIndicator from '../Loaders/LoadingIndicator';
import MapLoader from '../Wrappers/GoogleMapsWrapper';

// CONFIGURAZIONI
import { appConfig } from "../../config/appConfig";

const showMap = appConfig.ui.contactUsPage.showMap;

interface Props {
  contactForm: boolean;
  agencyId: number;
  showAgents: boolean;
}

interface MarkerInfo {
  name: string;
  adr: string;
  lat: number;
  lng: number;
}

const Contacts: React.FC<Props> = ({
  contactForm,
  agencyId,
}) => {
  const { t } = useTranslation();
  const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

  const [markerInfo, setMarkerInfo] = useState<MarkerInfo | null>(null);

  useEffect(() => {
    if (agency) {
      const addressParts = [
        agency.address_details?.address,
        agency.address_details?.city_name,
        agency.address_details?.sigla ? `(${agency.address_details.sigla})` : '',
      ].filter(Boolean);
      const fullAddress = addressParts.join(', ');

      setMarkerInfo({
        name: appConfig.map.markerData?.name || t('contactPage.map.markerDefaultName'),
        adr: fullAddress,
        lat: Number(appConfig.map.markerData?.lat),
        lng: Number(appConfig.map.markerData?.lng),
      });
    } else {
      setMarkerInfo(null);
    }
  }, [agency, t]);

  // --- Rendering Logic ---
  if (agencyLoading) return <LoadingIndicator />;
  if (agencyError) return <ServerError message={`${String(agencyError) || t('contactPage.errors.agencyLoadFailed')}`} />;

  return (
    <>
      <Helmet>
        <title>{t('contactPage.metaTitle')}</title>
        <meta name="description" content={t('contactPage.metaDescription') as string} />
      </Helmet>
      <SubHeaderDefault title={t('contactPage.pageTitle')} isSeo={true} />

      <section className="space-ptb">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="section-title text-center">
                <h2>{t('contactPage.title')}</h2>
                <p>{t('contactPage.paragraph')}</p>
                <p>{t('contactPage.para1')}</p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8">
              {contactForm && <WidgetMail />}
            </div>
            <div className="col-lg-4 mt-4 mt-md-0">
              <WidgetContacts
                agencyDataAddress={agency?.address_details?.address ?? ''}
                agencyDataCity={agency?.address_details?.city_name ?? ''}
                agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                agencyDataEmail={appConfig.agency.agencyEmail}
                agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                agencyDataPhone1={agency?.phone1 ?? ''}
                agencyDataPhone2={agency?.phone2 ?? ''}
                agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                agencyDataMobile={agency?.mobile ?? ''}
                agencyDataMobile2={agency?.mobile2 ?? ''}
                agencyDataFax={agency?.fax ?? ''}
                agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                agencyDataFacebook={appConfig.socialLinks.facebook}
                agencyDataInstagram={appConfig.socialLinks.instagram}
                agencyDataTwitter={appConfig.socialLinks.twitter}
                agencyDataLinkedin={appConfig.socialLinks.linkedin}
                agencyDataPinterest={appConfig.socialLinks.pinterest}
                agencyDataYoutube={appConfig.socialLinks.youtube}
              />
            </div>
          </div>
          <div className="row mt-4">
            <div className="col-12">
              {showMap && <MapLoader markerInfo={markerInfo} />}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contacts;
